import {
  CourseDateValue,
  getCourseDatesFromCourseEnrolmentScheduleItems,
} from '@duke-hq/lib/dates';
import { StudentScheduleItemType, StudentWorkSchedule } from '@duke-hq/svc-api-db/client';
import { djs } from '@duke-hq/djs';
import { getSemesterStartDate } from '@duke-hq/svc-api/student/services/getSemesterStartDate';

interface ScheduleItem {
  semester: number;
  position: number;
  started_at: Date | null;
  scheduled_start_date: Date;
  scheduled_due_date: Date;
  item_type: StudentScheduleItemType;
  item_id: string;
  is_complete: boolean;
  admin_course_schedule_week: number;
  unit_of_competency_id?: string | null;
}

// Progress counts theory completion. An ASSESSMENT/CONVERSATION row's is_complete
// now means theory-complete (the work-placement requirement is its own
// WORK_PLACEMENT_WORKBOOK row), so a student can reach 100% theory progress with a
// placement still outstanding.
export function isTheoryComplete(item: { is_complete: boolean }): boolean {
  return item.is_complete;
}

type CourseEnrolmentWithScheduleItems = {
  courseEnrolmentScheduleItems: ScheduleItem[];
  work_schedule: StudentWorkSchedule;
  start_at_date: Date;
  semester_2_start_at_date: Date | null;
  course_segment: string;
  created_at: Date;
};

// Progress is theory-only: an item counts as 100 once its theory is complete.
// Work placement is tracked separately (via the placement status) and does not
// hold back the progress percentage.
function getItemProgress(item: ScheduleItem): number {
  if (!isTheoryComplete(item)) {
    return 0;
  }
  return 100;
}

function buildProgressMaps(scheduleItems: ScheduleItem[]): ProgressMaps {
  return new Map(scheduleItems.map((item) => [item.item_id, getItemProgress(item)]));
}

type CourseEnrolmentScheduleItems = ScheduleItem[];

interface CourseMetrics {
  currentSemester: number;
  semesterProgress: number;
  // The current semester's due date, taken from the actual unit due dates (the
  // latest scheduled_due_date, minus one day) but never earlier than the course
  // formula end. This is written to the stored semester_due_date column and used
  // everywhere the due date is shown.
  semesterDueDate: CourseDateValue | null;
  expectedProgress: number;
  startDelay: number;
  // The whole-course target date, same basis as semesterDueDate.
  targetDate: CourseDateValue | null;
  firstActivity: Date | null;
}

type ProgressMaps = Map<string, number>;

function findCurrentSemester(courseEnrolment: CourseEnrolmentWithScheduleItems): number {
  const scheduleItems = courseEnrolment.courseEnrolmentScheduleItems;

  if (!scheduleItems?.length) {
    return 1;
  }

  const hasSemester2Items = scheduleItems.some(
    ({ item_type, semester }) => item_type !== StudentScheduleItemType.UNIT && semester === 2,
  );

  // Only roll over to semester 2 if there are actually semester 2 items
  if (hasSemester2Items) {
    const semester2StartDate = getSemesterStartDate(2, courseEnrolment);
    const today = djs();

    if (djs(semester2StartDate).isSameOrBefore(today, 'day')) {
      // Semester 2 date has passed, check if all semester 1 items are complete
      const allSemester1Complete = scheduleItems.every(
        ({ item_type, semester, is_complete }) =>
          item_type === StudentScheduleItemType.UNIT || semester !== 1 || is_complete,
      );

      if (allSemester1Complete) {
        return 2;
      }
    }
  }

  let lastStartedSemester: number | null = null;
  let hasPendingEarlierSemesters = false;

  // Process items in reverse chronological order
  for (let i = scheduleItems.length - 1; i >= 0; i--) {
    const item = scheduleItems[i];

    // Skip UNIT items and items with semester 0 (LLN)
    if (item === undefined || item.item_type === 'UNIT' || item.semester === 0) {
      continue;
    }

    // Track the most recent started semester
    if (item.started_at && lastStartedSemester === null) {
      lastStartedSemester = item.semester;
    }

    // Check if earlier semester (semester 1) has pending items
    if (item.semester === 1 && !item.is_complete) {
      hasPendingEarlierSemesters = true;
    }
  }

  // If no started items found
  if (lastStartedSemester === null) {
    return 1;
  }

  // If in semester 2 but has incomplete items from earlier semesters
  if (lastStartedSemester === 2 && hasPendingEarlierSemesters) {
    return 1;
  }

  // Return the most recent semester (default to 1 if undefined)
  return lastStartedSemester ?? 1;
}

function calculateStartDelay(
  firstItem: CourseEnrolmentScheduleItems[number] | null,
  enrolmentStartDate: Date,
): number {
  if (firstItem === null) {
    return 0;
  }
  const startDate = firstItem.started_at ?? enrolmentStartDate;
  return Math.max(0, djs(startDate).diff(firstItem.scheduled_start_date, 'days'));
}

function calculateMetrics(
  courseEnrolment: CourseEnrolmentWithScheduleItems,
  currentSemester: number,
  progressMaps: ProgressMaps,
): CourseMetrics {
  const {
    courseEnrolmentScheduleItems: scheduleItems = [],
    work_schedule,
    start_at_date,
    semester_2_start_at_date,
  } = courseEnrolment;

  let firstItem: CourseEnrolmentScheduleItems[number] | null = null;
  let lastItem: CourseEnrolmentScheduleItems[number] | null = null;
  let totalProgress = 0;
  let itemCount = 0;
  let currentScheduleItemIndex = -1;
  const semesterItems: CourseEnrolmentScheduleItems = [];

  for (const item of scheduleItems) {
    if (item.item_type === StudentScheduleItemType.UNIT) {
      continue;
    }
    if (firstItem === null || item.position < firstItem.position) {
      firstItem = item;
    }
    if (lastItem === null || item.position > lastItem.position) {
      lastItem = item;
    }

    // Progress is theory-only. A WORK_PLACEMENT_WORKBOOK row's is_complete tracks
    // the placement, not theory, so exclude it from the numerator/denominator (it
    // still contributes to firstItem/lastItem above for date maths).
    if (
      item.semester === currentSemester &&
      item.item_type !== StudentScheduleItemType.WORK_PLACEMENT_WORKBOOK
    ) {
      const progress = progressMaps.get(item.item_id) ?? 0;
      totalProgress += progress;
      itemCount++;

      if (currentScheduleItemIndex === -1 && item.scheduled_start_date >= new Date()) {
        currentScheduleItemIndex = semesterItems.length;
      }
      semesterItems.push(item);
    }
  }

  // The due date and target date come from the actual unit due dates, so they
  // move when someone adjusts the schedule, but never earlier than the course
  // formula end: a compressed schedule (e.g. CT/RPL) or an enrolment with no due
  // dates yet keeps the formula date. This is handled inside
  // getCourseDatesFromCourseEnrolmentScheduleItems.
  const courseStartAndEndDates = getCourseDatesFromCourseEnrolmentScheduleItems({
    courseEnrolmentScheduleItems: scheduleItems,
    courseSegment: courseEnrolment.course_segment,
    workSchedule: work_schedule,
    startAt: start_at_date,
    semesterTwoStartAt: semester_2_start_at_date,
  });

  return {
    currentSemester,
    semesterProgress: itemCount ? Math.trunc(totalProgress / itemCount) : 0,
    semesterDueDate:
      courseStartAndEndDates.semesters.find((item) => item.semester === currentSemester)
        ?.semesterEndDate ?? null,
    expectedProgress:
      currentScheduleItemIndex === -1
        ? 100
        : (currentScheduleItemIndex / semesterItems?.length) * 100,
    startDelay: calculateStartDelay(firstItem, start_at_date),
    targetDate: courseStartAndEndDates.courseEndDate,
    firstActivity: firstItem?.started_at ?? null,
  };
}

export function calculateEnrolmentMetrics(
  courseEnrolment: CourseEnrolmentWithScheduleItems,
): CourseMetrics {
  const { courseEnrolmentScheduleItems } = courseEnrolment;

  const sortedCourseEnrolmentScheduleItems = [...courseEnrolmentScheduleItems].sort(
    (a, b) => a.position - b.position,
  );

  const sortedEnrolment = {
    ...courseEnrolment,
    courseEnrolmentScheduleItems: sortedCourseEnrolmentScheduleItems,
  };

  const progressMaps = buildProgressMaps(sortedCourseEnrolmentScheduleItems);
  const currentSemester = findCurrentSemester(sortedEnrolment);
  return calculateMetrics(sortedEnrolment, currentSemester, progressMaps);
}

export function calculateEnrolmentMetricsBySemester(
  courseEnrolment: CourseEnrolmentWithScheduleItems,
  semester: number,
): CourseMetrics {
  const { courseEnrolmentScheduleItems } = courseEnrolment;
  const sortedCourseEnrolmentScheduleItems = [...courseEnrolmentScheduleItems].sort(
    (a, b) => a.position - b.position,
  );
  const progressMaps = buildProgressMaps(sortedCourseEnrolmentScheduleItems);

  return calculateMetrics(courseEnrolment, semester, progressMaps);
}
